import React, { useState } from "react";
import { motion, Variants } from "framer-motion";
import classes from "./PrepareQuestion.module.scss";
import TeacherComment from "../../molecules/teacher-commnet/TeacherComment";
import teacher from "../../static/images/teacher-comment.png";
import charimage1 from "../../static/images/prepare/char-question-1.png"
import charimage2 from "../../static/images/prepare/char-question-2.png"
import charimage3 from "../../static/images/prepare/char-question-3.png"
import charimage4 from "../../static/images/prepare/char-question-4.png"
import charimage5 from "../../static/images/prepare/char-question-5.png"
import charimage6 from "../../static/images/prepare/char-question-6.png"

export type PrepareQuestion = {
  questionNo: number;
  question: string[];
  teacherComment: string[];
  answerList: AnswerList[];
  greenComment: string[];
  blackComment: string[];
};

type AnswerList = { answerNo: number; answer: string; };

type Props = {
  content: PrepareQuestion;
  answerCallback: (questionNo: number, answerNo: number) => void;
  hidePrev: boolean;
  selectedNo?: number;
  currentQuestionNo: number;
  totalQuestionNo: number;

  // アニメ制御
  answersStartDelay: number; // 回答群の開始時刻
  commentsDelay: number;     // コメント群の開始時刻
  answerStagger: number;     // 回答のスタッガー間隔
  answerItemDur: number;     // 回答1件のduration
};

const buildContainerVariants = (
  delayChildren: number,
  staggerChildren: number
): Variants => ({
  hidden:  { opacity: 1 }, // 親は位置を動かさない
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      duration: 0.4,
      // 親のアニメ開始から「子の開始」までの遅延をここで一元管理
      delayChildren,
      staggerChildren,
      when: "beforeChildren", // 親→子の順序を安定させる
    },
  },
});

const answerItemVariants: Variants = {
  hidden:  { y: "20vh", opacity: 0 }, 
  visible: {
    y: 0,
    opacity: 1,
  },
};

const charImages: Record<number, string> = {
  1: charimage1,
  2: charimage2,
  3: charimage3,
  4: charimage4,
  5: charimage5,
  6: charimage6,
};


const PrepareQuestion = (props: Props) => {
  
  // ★ 追加：押された選択肢を一時的に保持（0.4秒だけ）
  const [pressedNo, setPressedNo] = useState<number | null>(null);
  const [locked, setLocked] = useState(false); // 二重クリック防止（任意だけど推奨）

  const handleClick = (answerNo: number) => {
    if (locked) return;
    setLocked(true);
    setPressedNo(answerNo); // ← 即ハイライト（0.4秒キープ）

    // 0.4秒後に親のコールバック実行（親で次遷移）
    setTimeout(() => {
      props.answerCallback(props.content.questionNo, answerNo);
      setPressedNo(null);   // 自分側の一時ハイライトは解除（次問に切り替わる想定）
      setLocked(false);
    }, 800);
  };

  const answers = props.content.answerList;
  return (
    <div className={classes.content}>
      {/* 回答群：親で delayChildren / staggerChildren を一元管理 */}
      <motion.div
        className={classes.answers}
        variants={buildContainerVariants(props.answersStartDelay, props.answerStagger)}
        initial="hidden"
        animate="visible"
      >
        {answers.map((elm, index) => {
          const activeNo = (pressedNo ?? props.selectedNo) ?? 0;
          const isActive = activeNo === elm.answerNo;
          return (
            <motion.div
              key={elm.answerNo}
              variants={answerItemVariants}
              transition={{ duration: props.answerItemDur }}
              className={`${classes.button} ${isActive ? classes.active : ""}`}
              onClick={() => handleClick(elm.answerNo)}
              aria-disabled={locked}>
              <span className={classes.no}>{index + 1}</span>
              <span className={classes.text}>{elm.answer}</span>
            </motion.div>
          );
        })}
      </motion.div>

      {/* コメント群：最後の回答の後に、まとめて下から */}
      <motion.div
        key={`comments-${props.currentQuestionNo}`}
        className={classes.commentsBlock}
        initial={{ y: "100vh", opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: props.answerItemDur, delay: props.commentsDelay }}
      >
        <div className={classes.greenComment}>{props.content.greenComment}</div>
        <div className={classes.blackComment}>{props.content.blackComment}</div>
        <div className={classes.comment}>
          <TeacherComment
            image={charImages[props.currentQuestionNo] ?? teacher}
            comment={props.content.teacherComment}
            charRight={props.currentQuestionNo === 1}
          />
        </div>
      </motion.div>
    </div>
  );
};

export default PrepareQuestion;
